import { coreDicts, type Bilingual, type Dict } from "./messages";

// Pure i18n core — no React. Shared by the dashboard LanguageProvider and the
// storefront (whose language comes from a per-page setting, not the toggle), and
// directly unit-testable.

export type Lang = "ms" | "en";

/**
 * Every string loaded so far, one flat table per language so a lookup is a single
 * object access. It starts with the storefront copy (./messages) and gains the
 * rest when loadFullMessages resolves.
 */
export const messages: Record<Lang, Dict> = { ms: {}, en: {} };

function addMessages(parts: Bilingual[]): void {
  for (const part of parts) {
    Object.assign(messages.ms, part.ms);
    Object.assign(messages.en, part.en);
  }
}
addMessages(coreDicts);

let full: Promise<void> | null = null;
let fullLoaded = false;
const fullListeners = new Set<() => void>();

/**
 * The rest of the app's copy — dashboard, admin, every page dictionary — fetched
 * as a chunk of its own. Lazy routes outside the buyer's path load it alongside
 * their own chunk (App.tsx lazyNamed), so no page renders before its words exist.
 * Safe to call any number of times; a failed load is retried on the next call.
 */
export function loadFullMessages(): Promise<void> {
  full ??= import("./messages/full").then(
    (m) => {
      addMessages(m.fullDicts);
      fullLoaded = true;
      for (const fn of fullListeners) fn();
    },
    (e) => {
      full = null;
      throw e;
    },
  );
  return full;
}

/** Runs `fn` once the full copy has arrived; returns the unsubscribe. */
export function onFullMessages(fn: () => void): () => void {
  fullListeners.add(fn);
  return () => fullListeners.delete(fn);
}
export const LANGS: Lang[] = ["ms", "en"];
export const DEFAULT_LANG: Lang = "ms";
export const LANG_LABELS: Record<Lang, string> = { ms: "BM", en: "EN" };

export const STORAGE_KEY = "ui_lang";

export function isLang(v: unknown): v is Lang {
  return v === "ms" || v === "en";
}

export function readStoredLang(): Lang | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isLang(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Language to boot with. A stored local choice ALWAYS wins over everything else,
 * so a refresh can never flip a user who picked English back to Malay. With no
 * stored choice we start on Malay and let the account preference (if any) load
 * in and adopt it — see {@link shouldAdoptAccountPref}.
 */
export function bootLang(stored: Lang | null): Lang {
  return stored ?? DEFAULT_LANG;
}

/**
 * Whether to adopt the account's saved language on load. Only when there is no
 * local choice yet (a fresh device) — otherwise the local choice is authoritative
 * and the server value must never override it (which would cause a refresh flip).
 */
export function shouldAdoptAccountPref(stored: Lang | null, accountPref: Lang | null): boolean {
  return stored === null && accountPref !== null;
}

export type TFn = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Pure lookup. Missing keys fall back to Malay, then to the key itself — a
 * missing translation never blanks the UI. `{name}`-style placeholders are
 * interpolated from `vars`.
 */
export function translate(
  lang: Lang,
  key: string,
  vars?: Record<string, string | number>,
): string {
  let str = messages[lang][key] ?? messages[DEFAULT_LANG][key];
  if (str === undefined) {
    // A page asked for a word outside what has loaded — a buyer page using a
    // dashboard key the static check could not see (a computed key). Fetch the
    // rest; the LanguageProvider re-renders when it lands.
    if (!fullLoaded) void loadFullMessages().catch(() => {});
    str = key;
  }
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      str = str.split(`{${k}}`).join(String(v));
    }
  }
  return str;
}
